import { FilterOutlined } from "@ant-design/icons";
import { PartnershipOffer, PartnershipTotalsFilter, PartnershipTransaction, PurchaseOrder, SDK } from "@axl-dev/school-sdk";
import { Button, Form, Popover, Typography } from "antd";
import cn from "classnames";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";

import { PartnershipProvider, usePartnership } from "@app/pages/partnership/model/partnership-provider";
import { AgreementBlocker } from "@app/pages/partnership/ui/components/AgreementBlocker/AgreementBlocker";
import { OfferTable } from "@app/pages/partnership/ui/components/Offers/OfferTable";
import { OrderTable } from "@app/pages/partnership/ui/components/Orders/OrderTable";
import { PartnershipTotalCard } from "@app/pages/partnership/ui/components/PartnershipTotalCard/PartnershipTotalCard";
import { TransactionTable } from "@app/pages/partnership/ui/components/Transactions/TransactionTable";
import { TransactionTotals } from "@app/pages/partnership/ui/components/Transactions/components/Totals/TransactionTotals";
import { TransferToBonus } from "@app/pages/partnership/ui/components/Transactions/components/TransferToBonus/TransferToBonus";
import { Withdrawal } from "@app/pages/partnership/ui/components/Transactions/components/Withdrawal/Withdrawal";
import { useDevice } from "@app/shared/lib/DeviceProvider";
import { useDrawer, useSchool, useUniqueKey } from "@app/shared/lib/hooks";
import { DateRangePicker, Loc } from "@app/shared/ui";
import { InputSearch } from "@app/shared/ui/InputSearch";
import { ClickIdentifiersSelector } from "@app/shared/ui/ClickIdentifiersSelector";
import { UtmTagsSelector } from "@app/shared/ui/UtmTagsSelector";

type BlockType = "totals" | "offers" | "orders" | "transactions";

export type PartnershipWidgetProps = {
  sdk: SDK;
  blockType: BlockType;
  titleFontSize: string;
  titleFontFamily: string;
  titleFontWeight: string;
  titleTextColor: string;
  showWithdrawalBtn: boolean;
  showTransferBonusBtn: boolean;
  showTransactionTotals: boolean;
  title?: string;
  // Prefetched
  prefetchedTotals?: any[];
  prefetchedOffers?: PartnershipOffer[];
  prefetchedOrders?: PurchaseOrder[];
  prefetchedTransactions?: PartnershipTransaction[];
  prefetchedTransactionTotals?: any[];
};

export const parsePartnershipProps = (container: HTMLElement): Omit<PartnershipWidgetProps, "sdk"> => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;
  return {
    blockType: attr("data-block-type", "totals") as BlockType,
    titleFontSize: attr("data-title-font-size", "20px"),
    titleFontFamily: attr("data-title-font-family"),
    titleFontWeight: attr("data-title-font-weight", "600"),
    titleTextColor: attr("data-title-text-color"),
    showWithdrawalBtn: attr("data-show-withdrawal-btn", "true") === "true",
    showTransferBonusBtn: attr("data-show-transfer-bonus-btn", "true") === "true",
    showTransactionTotals: attr("data-show-transaction-totals", "true") === "true",
    title: attr("data-title") || undefined,
  };
};

type BlockTitleProps = {
  children: React.ReactNode;
  titleFontSize?: string;
  titleFontFamily?: string;
  titleFontWeight?: string;
  titleTextColor?: string;
};

const BlockTitle: React.FC<BlockTitleProps> = ({ children, titleFontSize, titleFontFamily, titleFontWeight, titleTextColor }) => {
  const style: React.CSSProperties = { marginBottom: 0 };
  if (titleFontSize && titleFontSize !== "20px") style.fontSize = titleFontSize;
  if (titleFontWeight && titleFontWeight !== "600") style.fontWeight = titleFontWeight as any;
  if (titleFontFamily) style.fontFamily = titleFontFamily;
  if (titleTextColor) style.color = titleTextColor;
  return <Typography.Title level={2} style={style}>{children}</Typography.Title>;
};

// ============= Totals Block =============
type TotalsFilterFormProps = {
  filter: PartnershipTotalsFilter;
  onChange: (changes: PartnershipTotalsFilter) => void;
};

type PartnershipClickIdFields = {
  gclid: string[];
  fbclid: string[];
  msclkid: string[];
  ttclid: string[];
};

const getClickIdFields = (
  filter: PartnershipTotalsFilter,
): PartnershipClickIdFields => {
  const clickIds = filter as PartnershipTotalsFilter &
    Partial<PartnershipClickIdFields>;
  return {
    gclid: clickIds.gclid ?? [],
    fbclid: clickIds.fbclid ?? [],
    msclkid: clickIds.msclkid ?? [],
    ttclid: clickIds.ttclid ?? [],
  };
};

const hasClickIdChanges = (filter: PartnershipTotalsFilter) => {
  const clickIds = getClickIdFields(filter);
  return (
    clickIds.gclid.length > 0 ||
    clickIds.fbclid.length > 0 ||
    clickIds.msclkid.length > 0 ||
    clickIds.ttclid.length > 0
  );
};

const TotalsFilterForm: React.FC<TotalsFilterFormProps> = ({ filter: _filter, onChange }) => {
  const [form] = Form.useForm();
  const [filter, setFilter] = useState(new PartnershipTotalsFilter(_filter));

  const resetFilter = useCallback(() => {
    form.resetFields();
    onChange(new PartnershipTotalsFilter());
  }, [form, onChange]);

  return (
    <div style={{ minWidth: 280 }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ date: [filter.dateFrom, filter.dateTo] }}
        onFinish={() => onChange(filter)}
      >
        <Form.Item name="date" label={<Loc word="Global.date" default="Date" />}>
          <DateRangePicker
            showTime
            onChange={(dates) => {
              filter.update({
                // @ts-expect-error -- incorrect filter types
                dateFrom: moment(dates?.[0]?.toDate()),
                dateTo: moment(dates?.[1]?.toDate()),
              });
              setFilter(new PartnershipTotalsFilter(filter));
            }}
          />
        </Form.Item>
        <Form.Item name="utm" label={<Loc word="Partnership.analytics.utm" default="UTM" />}>
          <UtmTagsSelector
            defaultValue={{
              source: filter.utmSources,
              campaign: filter.utmCampaigns,
              medium: filter.utmMediums,
              content: filter.utmContents,
              term: filter.utmTerms,
            }}
            filter={{ direction: "first" }}
            onChange={(tags) => {
              filter.update({
                utmSources: tags.source,
                utmCampaigns: tags.campaign,
                utmMediums: tags.medium,
                utmContents: tags.content,
                utmTerms: tags.term,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- incorrect filter types
              } as any);
              setFilter(new PartnershipTotalsFilter(filter));
            }}
          />
        </Form.Item>
        <Form.Item name="clickIdentifiers" label={<Loc word="ClickIdentifiers" default="Click IDs" />}>
          <ClickIdentifiersSelector
            defaultValue={getClickIdFields(filter)}
            onChange={(ids) => {
              filter.update({
                ...ids,
              } as Partial<PartnershipTotalsFilter> & PartnershipClickIdFields);
              setFilter(new PartnershipTotalsFilter(filter));
            }}
          />
        </Form.Item>
      </Form>
      <footer className="flex align-center justify-end gap-10">
        <Button onClick={resetFilter}>
          <Loc word="Global.reset" default="Reset" />
        </Button>
        <Button type="primary" onClick={form.submit}>
          <Loc word="Global.apply" default="Apply" />
        </Button>
      </footer>
    </div>
  );
};

const TotalsBlock: React.FC<PartnershipWidgetProps> = (props) => {
  const [filter, setFilter] = useState(() => new PartnershipTotalsFilter());
  const [totals, setTotals] = useState<any[] | null>(props.prefetchedTotals || null);
  const [loading, setLoading] = useState(!props.prefetchedTotals);
  const filterPopover = useDrawer();

  const loadTotals = useCallback(async (f: PartnershipTotalsFilter) => {
    setLoading(true);
    try {
      const r = await props.sdk.api.partnership.fetchTotals(f);
      if (r.success && r.body?.items) setTotals(r.body.items);
    } finally {
      setLoading(false);
    }
  }, [props.sdk]);

  useEffect(() => {
    if (props.prefetchedTotals) {
      setTotals(props.prefetchedTotals);
      setLoading(false);
      return;
    }
    loadTotals(filter);
  }, [props.prefetchedTotals]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="flex align-center gap-20 mb-24" style={{ flexWrap: "wrap" }}>
        {props.title !== "" && (
          <BlockTitle titleFontSize={props.titleFontSize} titleFontFamily={props.titleFontFamily} titleFontWeight={props.titleFontWeight} titleTextColor={props.titleTextColor}>
            {props.title || <Loc word="Partnership.analytics" default="Analytics" />}
          </BlockTitle>
        )}
        <Popover
          open={filterPopover.visible}
          onOpenChange={(v) => (v ? filterPopover.open() : filterPopover.close())}
          title={<Loc word="Partnership.analytics.filter" default="Filter" />}
          content={
            <TotalsFilterForm
              filter={filter}
              onChange={(c) => {
                filterPopover.close();
                setFilter(c);
                loadTotals(c);
              }}
            />
          }
          showArrow={false}
          placement="bottomLeft"
          trigger="click"
          zIndex={1000}
          destroyTooltipOnHide
        >
          <Button
            icon={<FilterOutlined />}
            className={cn(
              filter.hasChanges || hasClickIdChanges(filter)
                ? "bg-success"
                : undefined,
            )}
          >
            <Loc word="Partnership.analytics.filter" default="Filter" />
          </Button>
        </Popover>
      </div>
      <div className="flex flex-wrap gap-20">
        {loading || totals === null
          ? [0, 1, 2, 3, 4].map((x) => <PartnershipTotalCard key={x} label="-" />)
          : totals.map((t) => (
            <PartnershipTotalCard
              key={t.type}
              value={t.value}
              valueType={t.valueType}
              label={<Loc word={`Partnership.member.${t.type}`} default={t.type} />}
            />
          ))}
      </div>
    </>
  );
};

// ============= Offers Block =============
const OffersBlock: React.FC<PartnershipWidgetProps> = (props) => {
  const [search, setSearch] = useState("");

  return (
    <>
      {props.title !== "" && (
        <div className="mb-24">
          <BlockTitle titleFontSize={props.titleFontSize} titleFontFamily={props.titleFontFamily} titleFontWeight={props.titleFontWeight} titleTextColor={props.titleTextColor}>
            {props.title || <Loc word="Partnership.offers" default="Offers" />}
          </BlockTitle>
        </div>
      )}
      <InputSearch onSearch={setSearch} />
      <OfferTable search={search} />
    </>
  );
};

// ============= Orders Block =============
const OrdersBlock: React.FC<PartnershipWidgetProps> = (props) => {
  return (
    <>
      {props.title !== "" && (
        <div className="mb-24">
          <BlockTitle titleFontSize={props.titleFontSize} titleFontFamily={props.titleFontFamily} titleFontWeight={props.titleFontWeight} titleTextColor={props.titleTextColor}>
            {props.title || <Loc word="Partnership.orders" default="Orders" />}
          </BlockTitle>
        </div>
      )}
      <OrderTable />
    </>
  );
};

// ============= Transactions Block =============
const TransactionsBlock: React.FC<PartnershipWidgetProps> = (props) => {
  const { partnership } = useSchool();
  const device = useDevice();
  const totalTrigger = useUniqueKey();

  const fetch = useCallback(() => {
    totalTrigger.update();
  }, [totalTrigger]);

  return (
    <>
      <div className={cn("mb-24 flex gap-10", device.deviceSize === "mobile" ? "flex-col" : "align-center")} style={{ flexWrap: "wrap" }}>
        {props.title !== "" && (
          <BlockTitle titleFontSize={props.titleFontSize} titleFontFamily={props.titleFontFamily} titleFontWeight={props.titleFontWeight} titleTextColor={props.titleTextColor}>
            {props.title || <Loc word="Partnership.transactions" default="Transactions" />}
          </BlockTitle>
        )}
        {props.showTransferBonusBtn && partnership?.transferFeeAllowedToBonus === true && (
          <div><TransferToBonus onSuccess={fetch} /></div>
        )}
        {props.showWithdrawalBtn && partnership?.transferFeeAllowed === true && (
          <div>
            <Withdrawal minAmount={partnership?.transferFeeMinAmount} onSuccess={fetch} />
          </div>
        )}
      </div>
      {props.showTransactionTotals && (
        <div className="mb-24">
          <TransactionTotals key={totalTrigger.key} />
        </div>
      )}
      <TransactionTable />
    </>
  );
};

// ============= Main Widget =============
const PartnershipWidgetInner: React.FC<PartnershipWidgetProps> = (props) => {
  const { loading, currentMember } = usePartnership();

  if (loading.root) return null;

  const block = (
    <>
      {props.blockType === "totals" && <TotalsBlock {...props} />}
      {props.blockType === "offers" && <OffersBlock {...props} />}
      {props.blockType === "orders" && <OrdersBlock {...props} />}
      {props.blockType === "transactions" && <TransactionsBlock {...props} />}
    </>
  );

  if (!currentMember) return block;

  return <AgreementBlocker>{block}</AgreementBlocker>;
};

export const PartnershipWidget: React.FC<PartnershipWidgetProps> = (props) => {
  return (
    <PartnershipProvider>
      <PartnershipWidgetInner {...props} />
    </PartnershipProvider>
  );
};
